import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ExternalLink, Users, Zap, HardDrive, Search, ChevronDown, MoreHorizontal,
  CircleDollarSign, PauseCircle, PlayCircle, Trash2, AlertTriangle,
} from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const PLAN_FILTERS = [
  { key: "all", label: "Alle plannen" },
  { key: "Pro", label: "Pro" },
  { key: "Free", label: "Free" },
];

const ROLE_BADGE = {
  owner:    "bg-[#1a1a1a] text-white border-transparent",
  manager:  "bg-amber-50 text-amber-700 border-amber-200",
  creator:  "bg-sky-50 text-sky-700 border-sky-200",
  viewer:   "bg-gray-100 text-gray-600 border-gray-200",
};

function formatBytes(n) {
  if (!n || n < 1024) return `${n || 0} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("nl-NL", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso.slice(0, 10); }
}

function fmtDateTime(iso) {
  if (!iso) return "Nog nooit";
  try { return new Date(iso).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
}

export default function WorkspacesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [planFilter, setPlanFilter] = useState("all");
  const [expanded, setExpanded] = useState({}); // {brand_id: true}
  const [busyId, setBusyId] = useState(null);
  const [deleting, setDeleting] = useState(null); // {id, name}
  const navigate = useNavigate();

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/super-admin/workspaces");
      setRows(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kon workspaces niet laden");
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (planFilter !== "all" && r.plan !== planFilter) return false;
      if (!q) return true;
      return `${r.name} ${r.slug || ""}`.toLowerCase().includes(q);
    });
  }, [rows, query, planFilter]);

  const accessHub = async (row) => {
    setBusyId(row.id);
    try {
      const { data } = await api.post("/super-admin/impersonate", { brand_id: row.id });
      localStorage.setItem("bh_imp_token", data.token);
      localStorage.setItem("bh_imp_brand", JSON.stringify(data.brand));
      toast.success(`God Mode actief in ${row.name}`);
      navigate("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kon God Mode niet activeren");
    } finally { setBusyId(null); }
  };

  const toggleSuspend = async (row) => {
    try {
      await api.post(`/super-admin/workspaces/${row.id}/suspend`, { suspend: !row.is_suspended });
      toast.success(row.is_suspended ? "Workspace heractiveerd" : "Workspace op pauze");
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Mislukt"); }
  };

  return (
    <div data-testid="sa-workspaces">
      <Header title="Workspaces" sub="Alle merken op het platform. Klik Access Hub voor tijdelijke owner-rechten." />

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" strokeWidth={1.75} />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Zoek op naam of slug…"
            className="h-10 bg-white border-gray-200 pl-9 rounded-[var(--global-radius)]"
            data-testid="sa-workspaces-search"
          />
        </div>
        <div className="flex items-center gap-1 bg-[#f3f3f1] rounded-[var(--global-radius)] p-1">
          {PLAN_FILTERS.map((f) => {
            const active = planFilter === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setPlanFilter(f.key)}
                data-testid={`sa-workspaces-filter-${f.key}`}
                className={`h-8 px-3 rounded-[calc(var(--global-radius)-2px)] text-xs font-medium transition-colors ${
                  active ? "bg-white text-[#1a1a1a] shadow-sm" : "text-gray-600 hover:text-[#1a1a1a]"
                }`}
              >{f.label}</button>
            );
          })}
        </div>
      </div>

      <div className="rounded-[var(--global-radius-xl)] border border-gray-200 bg-white overflow-hidden">
        <div className="grid grid-cols-[24px_1fr_90px_110px_90px_90px_90px_120px] gap-3 px-5 py-3 border-b border-gray-100 text-[11px] uppercase tracking-widest text-gray-500">
          <div />
          <div>Brand</div>
          <div>Plan</div>
          <div>Aangemaakt</div>
          <div className="flex items-center gap-1"><Users className="w-3 h-3" strokeWidth={1.75} /> Users</div>
          <div className="flex items-center gap-1"><HardDrive className="w-3 h-3" strokeWidth={1.75} /> Storage</div>
          <div className="flex items-center gap-1"><Zap className="w-3 h-3" strokeWidth={1.75} /> AI-calls</div>
          <div className="text-right">Actie</div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-gray-500">Laden…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">
            {rows.length === 0 ? "Nog geen workspaces." : "Geen workspaces matchen de filter."}
          </div>
        ) : (
          filtered.map((r) => (
            <WorkspaceRow
              key={r.id}
              row={r}
              expanded={!!expanded[r.id]}
              toggleExpanded={() => setExpanded((e) => ({ ...e, [r.id]: !e[r.id] }))}
              onAccess={() => accessHub(r)}
              onToggleSuspend={() => toggleSuspend(r)}
              onDelete={() => setDeleting({ id: r.id, name: r.name })}
              busy={busyId === r.id}
            />
          ))
        )}
      </div>

      <DeleteModal
        target={deleting}
        onClose={() => setDeleting(null)}
        onConfirmed={async () => { setDeleting(null); await refresh(); }}
      />
    </div>
  );
}

function WorkspaceRow({ row, expanded, toggleExpanded, onAccess, onToggleSuspend, onDelete, busy }) {
  return (
    <div className="border-b border-gray-50 last:border-0" data-testid={`sa-workspace-row-${row.id}`}>
      <div
        className="grid grid-cols-[24px_1fr_90px_110px_90px_90px_90px_120px] gap-3 px-5 py-3.5 items-center text-sm cursor-pointer hover:bg-gray-50/60 transition-colors"
        onClick={toggleExpanded}
      >
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? "rotate-180" : ""}`} strokeWidth={1.75} />
        <div className="min-w-0">
          <div className="font-medium text-[#1a1a1a] flex items-center gap-2 truncate">
            {row.name}
            {row.is_suspended && (
              <span className="text-[10px] uppercase tracking-widest font-semibold text-red-600 bg-red-50 border border-red-100 px-1.5 py-0.5 rounded-sm">Pauze</span>
            )}
          </div>
          <div className="text-xs text-gray-400 truncate">{row.slug}</div>
        </div>
        <div>
          <PlanBadge plan={row.plan} />
        </div>
        <div className="text-gray-600">{fmtDate(row.created_at)}</div>
        <div className="text-gray-600 tabular-nums">{row.active_users}</div>
        <div className="text-gray-600 tabular-nums">{formatBytes(row.storage_bytes)}</div>
        <div className="text-gray-600 tabular-nums">{row.ai_credits_used}</div>
        <div className="text-right flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
          <button
            disabled={busy}
            onClick={onAccess}
            data-testid={`sa-access-hub-${row.id}`}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-[var(--global-radius)] bg-[var(--global-action)] text-white text-xs font-medium hover:opacity-90 disabled:opacity-50"
          >
            <ExternalLink className="w-3 h-3" strokeWidth={2} /> Access Hub
          </button>
          <ActionsMenu row={row} onToggleSuspend={onToggleSuspend} onDelete={onDelete} />
        </div>
      </div>

      {expanded && <ExpandedUsers brandId={row.id} />}
    </div>
  );
}

function PlanBadge({ plan }) {
  const isPro = plan === "Pro";
  return (
    <span
      data-testid={`sa-plan-badge-${plan}`}
      className={`inline-flex items-center h-6 px-2 rounded-[var(--global-radius)] text-[11px] font-medium border ${
        isPro
          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
          : "bg-gray-50 text-gray-600 border-gray-200"
      }`}
    >
      {plan}
    </span>
  );
}

function ActionsMenu({ row, onToggleSuspend, onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const stripeUrl = row.stripe_customer_url;
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`sa-actions-${row.id}`}
        className="w-8 h-8 rounded-[var(--global-radius)] border border-gray-200 bg-white hover:bg-gray-50 flex items-center justify-center text-gray-500"
      >
        <MoreHorizontal className="w-4 h-4" strokeWidth={1.75} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-56 rounded-[var(--global-radius-lg)] border border-gray-200 bg-white shadow-lg z-20 p-1" data-testid={`sa-actions-menu-${row.id}`}>
          <a
            href={stripeUrl || "#"}
            target={stripeUrl ? "_blank" : undefined}
            rel="noopener noreferrer"
            onClick={(e) => { if (!stripeUrl) { e.preventDefault(); return; } setOpen(false); }}
            data-testid={`sa-action-stripe-${row.id}`}
            className={`flex items-center gap-2 px-3 h-9 rounded-[var(--global-radius)] text-sm ${
              stripeUrl ? "text-[#1a1a1a] hover:bg-gray-50" : "text-gray-300 cursor-not-allowed"
            }`}
          >
            <CircleDollarSign className="w-4 h-4" strokeWidth={1.75} />
            View in Stripe
          </a>
          <button
            onClick={() => { setOpen(false); onToggleSuspend(); }}
            data-testid={`sa-action-suspend-${row.id}`}
            className="w-full flex items-center gap-2 px-3 h-9 rounded-[var(--global-radius)] text-sm text-[#1a1a1a] hover:bg-gray-50"
          >
            {row.is_suspended
              ? <><PlayCircle className="w-4 h-4" strokeWidth={1.75} /> Workspace heractiveren</>
              : <><PauseCircle className="w-4 h-4" strokeWidth={1.75} /> Suspend workspace</>}
          </button>
          <div className="h-px bg-gray-100 my-1" />
          <button
            onClick={() => { setOpen(false); onDelete(); }}
            data-testid={`sa-action-delete-${row.id}`}
            className="w-full flex items-center gap-2 px-3 h-9 rounded-[var(--global-radius)] text-sm text-red-600 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4" strokeWidth={1.75} /> Delete workspace
          </button>
        </div>
      )}
    </div>
  );
}

function ExpandedUsers({ brandId }) {
  const [users, setUsers] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/super-admin/workspaces/${brandId}/users`);
        setUsers(data);
      } catch { setUsers([]); }
    })();
  }, [brandId]);

  return (
    <div className="bg-[#fafafa] border-t border-gray-100 px-5 py-4" data-testid={`sa-expanded-${brandId}`}>
      <div className="grid grid-cols-[1fr_120px_180px] gap-3 px-3 pb-2 text-[10px] uppercase tracking-widest text-gray-400">
        <div>User</div>
        <div>Rol</div>
        <div>Laatst ingelogd</div>
      </div>
      {!users ? (
        <div className="px-3 py-4 text-xs text-gray-500">Laden…</div>
      ) : users.length === 0 ? (
        <div className="px-3 py-4 text-xs text-gray-500">Geen users gevonden.</div>
      ) : (
        users.map((u) => (
          <div key={u.user_id} className="grid grid-cols-[1fr_120px_180px] gap-3 px-3 py-2 items-center text-sm">
            <div className="min-w-0">
              <div className="font-medium text-[#1a1a1a] truncate">{u.name}</div>
              <div className="text-xs text-gray-500 truncate">{u.email}</div>
            </div>
            <div>
              <span className={`inline-flex items-center h-6 px-2 rounded-[var(--global-radius)] text-[11px] font-medium border capitalize ${ROLE_BADGE[u.role] || ROLE_BADGE.viewer}`}>
                {u.role}
              </span>
            </div>
            <div className="text-gray-600 text-xs">{fmtDateTime(u.last_login_at)}</div>
          </div>
        ))
      )}
    </div>
  );
}

function DeleteModal({ target, onClose, onConfirmed }) {
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setConfirm(""); }, [target?.id]);

  const canConfirm = target && confirm.trim() === target.name;

  const doDelete = async () => {
    if (!canConfirm) return;
    setBusy(true);
    try {
      await api.delete(`/super-admin/workspaces/${target.id}`, {
        data: { confirm_name: confirm.trim() },
      });
      toast.success("Workspace verwijderd");
      await onConfirmed();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verwijderen mislukt");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!target} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="sa-delete-modal" className="bg-white rounded-[var(--global-radius-xl)]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-5 h-5" strokeWidth={1.75} /> Workspace permanent verwijderen
          </DialogTitle>
        </DialogHeader>
        <div className="text-sm text-gray-600 space-y-3">
          <p>Dit verwijdert <strong>{target?.name}</strong>, alle pagina&apos;s, bestanden, memberships, abonnementen en chatberichten. Deze actie is onomkeerbaar.</p>
          <p className="text-xs text-gray-500">Typ de naam van de workspace om te bevestigen:</p>
          <div className="font-mono text-xs text-gray-400">{target?.name}</div>
          <Input
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder={target?.name}
            className="bg-white"
            autoFocus
            data-testid="sa-delete-confirm-input"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-[var(--global-radius)]">Annuleer</Button>
          <Button
            disabled={!canConfirm || busy}
            onClick={doDelete}
            data-testid="sa-delete-confirm-btn"
            className="rounded-[var(--global-radius)] bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
          >{busy ? "Bezig…" : "Permanent verwijderen"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function Header({ title, sub }) {
  return (
    <div className="mb-8">
      <h1 className="font-display text-3xl font-medium text-[#1a1a1a] mb-1">{title}</h1>
      {sub && <p className="text-sm text-gray-500">{sub}</p>}
    </div>
  );
}
